// Captures UTM parameters from the URL and persists them in cookies + Mixpanel super properties.
// First-touch is write-once (90-day cookie). Last-touch always overwrites (30-day cookie).
// These are non-essential cookies, so nothing is written without analytics consent.

import { hasConsent } from '../consent/consentState';

const FIRST_TOUCH_COOKIE = 'hiiker_first_touch';
const LAST_TOUCH_COOKIE = 'hiiker_last_touch';
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

interface UtmData {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  referrer?: string;
  landing_page?: string;
  captured_at?: string;
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name: string, value: string, days: number): void {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function parseUtmFromUrl(): UtmData | null {
  const params = new URLSearchParams(window.location.search);
  const source = params.get('utm_source');
  if (!source) return null;

  const data: UtmData = { utm_source: source, captured_at: new Date().toISOString() };
  if (params.get('utm_medium')) data.utm_medium = params.get('utm_medium')!;
  if (params.get('utm_campaign')) data.utm_campaign = params.get('utm_campaign')!;
  if (params.get('utm_content')) data.utm_content = params.get('utm_content')!;
  if (params.get('utm_term')) data.utm_term = params.get('utm_term')!;
  if (document.referrer) data.referrer = document.referrer;
  data.landing_page = window.location.href;

  return data;
}

export function getFirstTouchData(): UtmData | null {
  const raw = getCookie(FIRST_TOUCH_COOKIE);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

export function getLastTouchData(): UtmData | null {
  const raw = getCookie(LAST_TOUCH_COOKIE);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

export function captureAttribution(): void {
  if (!hasConsent('analytics')) return;

  const utmData = parseUtmFromUrl();
  if (!utmData) return;

  // First-touch: write-once
  if (!getCookie(FIRST_TOUCH_COOKIE)) {
    setCookie(FIRST_TOUCH_COOKIE, JSON.stringify(utmData), 90);
  }

  // Last-touch: always overwrite
  setCookie(LAST_TOUCH_COOKIE, JSON.stringify(utmData), 30);

  // Set Mixpanel super properties so every event in this session includes UTM data
  if (window.mixpanel) {
    const superProps: Record<string, string> = {};
    if (utmData.utm_source) superProps['UTM Source'] = utmData.utm_source;
    if (utmData.utm_medium) superProps['UTM Medium'] = utmData.utm_medium;
    if (utmData.utm_campaign) superProps['UTM Campaign'] = utmData.utm_campaign;
    if (utmData.utm_content) superProps['UTM Content'] = utmData.utm_content;
    if (utmData.utm_term) superProps['UTM Term'] = utmData.utm_term;
    window.mixpanel.register(superProps);
  }
}

// Auto-run on import
captureAttribution();
